import { IdentityContext } from "@examspark/types";

declare global {
  interface Window {
    __ES_IDENTITY_CONTEXT?: IdentityContext;
  }
}

export type AppStateLike = {
  user?: { id: string };
  workspace?: { id: string };
  app?: { id: string };
  proxiedApp?: { id: string };
  useSystemAdmin?: boolean | undefined;
};

type Headers = Record<string, string | undefined>;

export class IdentityContextProvider {
  private static emitter = new EventTarget();

  public static get EMPTY(): IdentityContext {
    return { userId: null, workspaceId: null, appId: null };
  }

  private static _context: IdentityContext | undefined;

  static getContext() {
    if (typeof window === "undefined") {
      return this._context ?? this.EMPTY;
    }

    return window.__ES_IDENTITY_CONTEXT ?? this.EMPTY;
  }

  static getContextAsHeaders(overrides?: Partial<IdentityContext>) {
    return getContextAsHeaders({
      ...this.getContext(),
      ...overrides,
    });
  }

  static setContext(contextValues: IdentityContext) {
    const context = this.getContext() ?? this.EMPTY;

    Object.assign(context, contextValues);

    if (typeof window === "undefined") {
      // we're in a node environment
      this._context = context;
      return;
    }

    window.__ES_IDENTITY_CONTEXT = context;

    this.emitter.dispatchEvent(new CustomEvent("change", { detail: context }));
  }

  // for the cms
  static setContextFromState(state: AppStateLike) {
    this.setContext({
      userId: state.user?.id ?? null,
      workspaceId: state.workspace?.id ?? null,
      appId: state.app?.id ?? null,
      useSystemAdmin: state.useSystemAdmin,
    });
  }

  static setContextFromHeaders(headers: Headers) {
    this.setContext(readContextFromHeaders(headers));
  }

  static on(event: "change", listener: (context: IdentityContext) => void) {
    const ref = (event: any) => listener(event.detail);

    this.emitter.addEventListener(event, ref);
    return () => {
      this.emitter.removeEventListener(event, ref);
    };
  }
}

export const getContextAsHeaders = (context: IdentityContext) => {
  const headers: Record<string, string> = {};
  const { appId, workspaceId, userId, useSystemAdmin } = context;

  if (userId != null) {
    headers["x-es-identity-userid"] = userId;
  }
  if (workspaceId != null) {
    headers["x-es-identity-workspaceid"] = workspaceId;
  }
  if (appId != null) {
    headers["x-es-identity-appid"] = appId;
  }
  if (useSystemAdmin) {
    /** Enable to disregard caller's workspace membership */
    headers["x-es-systemadmin"] = "true";
  }

  return headers;
};

export const readContextFromHeaders = (headers: Headers | undefined) => {
  const contextValues: IdentityContext = {
    userId: null,
    workspaceId: null,
    appId: null,
    useSystemAdmin: undefined,
  };

  if (headers == null) return contextValues;

  if (headers["x-es-identity-userid"]) {
    contextValues.userId = headers["x-es-identity-userid"];
  }
  if (headers["x-es-identity-workspaceid"]) {
    contextValues.workspaceId = headers["x-es-identity-workspaceid"];
  }
  if (headers["x-es-identity-appid"]) {
    contextValues.appId = headers["x-es-identity-appid"];
  }
  if (
    headers["x-es-systemadmin"] === "true" ||
    headers["x-es-systemadmin"] === "false"
  ) {
    contextValues.useSystemAdmin = headers["x-es-systemadmin"] === "true";
  }

  return contextValues;
};
